import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Database, Layout, Upload, GitMerge, Users, Building2, LogOut, Menu, X, Settings, ChevronDown, Network } from "lucide-react";
import { useState } from "react";

const nav = (slug, isAdmin) => {
  const items = [
    { to: `/t/${slug}/ask`, label: "Ask PeopleIQ", icon: <Sparkles size={16} />, testId: "nav-ask", highlight: true },
    { to: `/t/${slug}/dashboards`, label: "Dashboards", icon: <Layout size={16} />, testId: "nav-dashboards" },
    { to: `/t/${slug}/orgchart`, label: "Org Chart", icon: <Network size={16} />, testId: "nav-orgchart" },
    { to: `/t/${slug}/datasets`, label: "Datasets", icon: <Database size={16} />, testId: "nav-datasets" },
  ];
  if (isAdmin) {
    items.push(
      { to: `/t/${slug}/upload`, label: "Upload Data", icon: <Upload size={16} />, testId: "nav-upload" },
      { to: `/t/${slug}/join`, label: "Join Datasets", icon: <GitMerge size={16} />, testId: "nav-join" },
      { to: `/t/${slug}/team`, label: "Team Directory", icon: <Users size={16} />, testId: "nav-team" },
      { to: `/t/${slug}/members`, label: "Members & Roles", icon: <Users size={16} />, testId: "nav-members" },
      { to: `/t/${slug}/settings`, label: "Company Settings", icon: <Settings size={16} />, testId: "nav-settings" },
    );
  }
  return items;
};

export default function AppShell() {
  const { user, company, logout } = useAuth();
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === "company_admin" || user?.role === "platform_admin";
  const items = nav(slug, isAdmin);
  const handleLogout = () => { logout(); navigate("/login"); };
  const initials = (company?.name || "P").split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join("");

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Sidebar */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky lg:top-0 z-40 top-0 left-0 h-screen w-64 bg-[var(--surface)] border-r border-[var(--border)] flex flex-col transition-transform duration-200 ease-out`}>
        <div className="px-5 py-5 border-b border-[var(--border)]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm" style={{ background: company?.primary_color || "var(--primary)" }}>
                {company?.logo_url ? <img src={company.logo_url} alt="" className="w-full h-full rounded-lg object-cover" /> : initials}
              </div>
              <div>
                <div className="font-display text-[15px] font-semibold leading-tight" data-testid="company-name">{company?.name}</div>
                <div className="text-[11px] mono text-[var(--text-muted)]">{company?.slug}.peopleiq</div>
              </div>
            </div>
            <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={18} /></button>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">Powered by PeopleIQ</div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto scroll-thin">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              onClick={() => setOpen(false)}
              end={it.to.endsWith("/ask")}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                  isActive
                    ? "bg-[var(--primary-soft)] text-[var(--primary)] font-medium"
                    : it.highlight
                      ? "text-slate-700 hover:bg-[var(--surface-2)] font-medium"
                      : "text-slate-600 hover:bg-[var(--surface-2)] hover:text-slate-900"
                }`
              }
            >
              {it.icon}
              <span>{it.label}</span>
              {it.highlight && (
                <span className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[var(--primary)] text-white">AI</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[var(--border)] relative">
          <button onClick={() => setUserOpen(!userOpen)} className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-[var(--surface-2)] transition-colors" data-testid="user-menu">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
            </div>
            <ChevronDown size={14} className="text-slate-400" />
          </button>
          {userOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-[var(--border)] rounded-lg shadow-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-[var(--border)]">
                <div className="chip capitalize" data-testid="user-role">{user?.role?.replace("_", " ")}</div>
              </div>
              <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-[var(--surface-2)] transition-colors">
                <LogOut size={14} /> Log out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-[var(--border)] h-14 px-4 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => setOpen(true)} data-testid="sidebar-toggle" className="text-slate-600"><Menu size={20} /></button>
          <div className="font-display font-semibold">{company?.name}</div>
          <div className="w-6" />
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
